import type {
  VacancyListResponse,
  VacancyRequest,
  VacancyResponse,
} from '~/types/__generated__'
import type { Strapi4RequestParams } from '~/types/strapi'

const FULL_VACANCY_POPULATE = [
  'image',
  'address',
  'media',
  'media.items',
  'content',
  'company',
  'company.address',
  'company.profile',
  'company.profile.topCultureRanking',
  'company.profile.minCultureRanking',
  'company.logo',
  'department',
  'tags',
  'tags.parent',
  'tags.parent.parent',
  'tags.children',
  'filterTags',
  'filterTags.tag',
  'manager',
  'profile',
  'profile.education',
  'profile.rolesRanking',
  'profile.motivationsRanking',
  'location',
  'meta',
  'meta.image',
  'meta.socials',
  'meta.socials.image',
]

const OVERVIEW_VACANCY_POPULATE = [
  'tags',
  'tags.parent',
  'tags.children',
  'company',
  'company.logo',
  'company.address',
  'image',
  'media',
  'media.items',
]

export function useApiVacancy() {
  const client = useStrapiClient()

  async function getVacancies(query: Strapi4RequestParams = {}) {
    const response = await client<VacancyListResponse>('/vacancies', {
      method: 'GET',
      params: {
        populate: OVERVIEW_VACANCY_POPULATE,
        ...query,
      },
    })
    return response
  }

  async function getVacancyBySlug(
    slug: string,
    query: Strapi4RequestParams = {},
  ) {
    const { data } = await client<VacancyListResponse>('/vacancies', {
      method: 'GET',
      params: {
        populate: FULL_VACANCY_POPULATE,
        useGeoFilter: false,
        useTagFilter: false,
        useInteractionsFilter: false,
        ...query,
        filters: {
          slug: { $eq: slug },
        },
      },
    })

    return data?.[0]
  }

  async function getSimilarVacancies(
    id: number | string,
    query: Strapi4RequestParams = {},
  ) {
    const { data } = await client<VacancyListResponse>(
      `/vacancies/${id}/similar`,
      {
        method: 'GET',
        params: {
          populate: OVERVIEW_VACANCY_POPULATE,
          ...query,
        },
      },
    )
    return data
  }

  async function createVacancy(
    body: VacancyRequest | FormData,
    query: Strapi4RequestParams = {},
  ) {
    const { data } = await client<VacancyResponse>(`/vacancies`, {
      method: 'POST',
      body,
      params: {
        populate: FULL_VACANCY_POPULATE,
        ...query,
      },
    })
    return data
  }

  async function updateVacancy(
    id: number | string,
    body: VacancyRequest | FormData,
    query: Strapi4RequestParams = {},
  ) {
    const { data } = await client<VacancyResponse>(`/vacancies/${id}`, {
      method: 'PUT',
      body,
      params: {
        populate: FULL_VACANCY_POPULATE,
        ...query,
      },
    })
    return data
  }

  async function deleteVacancy(id: number | string) {
    const response = await client<VacancyResponse>(`/vacancies/${id}`, {
      method: 'DELETE',
    })
    return response
  }

  return {
    getVacancies,
    getVacancyBySlug,
    getSimilarVacancies,
    createVacancy,
    updateVacancy,
    deleteVacancy,
  }
}
